import { OnInit, Component, ViewEncapsulation, ViewChild } from '@angular/core';
import { IInvoiceAddress } from '../../interfaces/invoice-address';
import { IInvoiceAddressForm } from '../../interfaces/invoice-address-form';
import { NoEmptyString } from '../../interfaces/no-empty-string.validator';
import { InvoiceAddressService } from '../../services/invoice-address.service';
import { UntypedFormGroup, UntypedFormControl, Validators } from '@angular/forms';
import { Message } from '../../classes/Message';
import { ActivatedRoute, Params, Router } from '@angular/router';
import { SelectItem, MessageService } from 'primeng';
import { OrganisationsService } from '../../services/organisations.service';
import { UserService } from '../../services/user.service';
import { getCombinedParameters } from '../../utils/getCombinedParameters';
import { tryParseInt } from '../../utils/tryParseInt';
import { InputHelper } from '../../utils/input.helper';
import { AddressAutoCompletionService } from '../../services/address-auto-completion.service';

@Component({
  selector: 'invoice-address',
  templateUrl: './invoice-address.component.html',
  styleUrls: ['./invoice-address.component.scss'],
  //encapsulation: ViewEncapsulation.None
})
export class InvoiceAddressComponent implements OnInit {
  public invoiceAddressId: number;
  public invoiceAddressData: IInvoiceAddress;
  public invoiceAddressForm: IInvoiceAddressForm;
  public invoiceAddressFormGroup: UntypedFormGroup;
  public isVmeMode: boolean;
  public originalCustomerorgId: number;
  public originalCurrentOrgName: string;
  public customerorgId: number; //dynamic for VME Users
  public currentOrgName: string; //dynamic for VME Users
  public currentUserOrgTypeId: number;
  public currentUserOrgId: number;
  public renderView: boolean = false;
  public isSaving: boolean = false;

  private defaultBillingWaitDays: number = 7;
  //values are taken from Xero API (enum Xero.Api.Core.Model.Types.PaymentTermType)
  paymentTermsTypes: SelectItem[] = [
    { label: '', value: -1 },
    { label: 'Following Month', value: 0 },
    { label: 'After Bill Date', value: 1 },
    { label: 'Current Month', value: 2 },
    { label: 'Days After Bill Month', value: 3 },
    { label: 'After Invoice Month', value: 4 },
  ];
  countries: SelectItem[] = [{ label: 'United Kingdom', value: 'UK' }]; // using UK as per current implementation until we have a countries table
  constructor(
    private invoiceAddressService: InvoiceAddressService,
    private userService: UserService,
    private route: ActivatedRoute,
    private router: Router,
    private messageService: MessageService,
    private organisationService: OrganisationsService,
    public inputHelper: InputHelper,
    private addressAutoCompletionService: AddressAutoCompletionService,
  ) {}
  ngOnInit() {
    getCombinedParameters(this.route).subscribe((params) => {
      this.invoiceAddressId = tryParseInt(params['id']) as number;
      this.customerorgId = tryParseInt(params['orgid']) as number;
      this.userService.getCurrentUserOrg().then((res) => {
        this.currentUserOrgId = this.originalCustomerorgId = res.id;
        this.currentUserOrgTypeId = res.organisationTypeId;
        this.isVmeMode = res.organisationTypeId == 1;
        this.originalCurrentOrgName = res.name;
        if (!this.invoiceAddressId) this.currentOrgName = this.originalCurrentOrgName;
      });
      if (this.invoiceAddressId) {
        this.getInvoiceAddressData();
      } else {
        this.initialiseInvoiceAddressForm();
      }
    });

    this.initAddressAutoCompletion();
  }

  onOrganisationChange(orgId: number) {
    if (orgId) {
      this.organisationService.getOrg(orgId).then((result) => {
        this.currentOrgName = result.name;
        this.currentUserOrgId = orgId;
      });
    } else {
      this.currentUserOrgId = this.originalCustomerorgId;
      this.currentOrgName = this.originalCurrentOrgName;
    }
  }

  initialiseInvoiceAddressForm() {
    const regexp = new RegExp(
      /^(([^<>()\[\]\\.,;:\s@"]+(\.[^<>()\[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/,
    );
    this.invoiceAddressForm = {
      addressLine1: new UntypedFormControl(
        this.invoiceAddressData ? this.invoiceAddressData.addressLine1 : '',
        Validators.compose([Validators.required, Validators.maxLength(60), NoEmptyString]),
      ),
      addressLine2: new UntypedFormControl(
        this.invoiceAddressData ? this.invoiceAddressData.addressLine2 : '',
        Validators.compose([null, Validators.maxLength(60)]),
      ),
      addressLine3: new UntypedFormControl(
        this.invoiceAddressData ? this.invoiceAddressData.addressLine3 : '',
        Validators.compose([null, Validators.maxLength(60)]),
      ),
      town: new UntypedFormControl(
        this.invoiceAddressData ? this.invoiceAddressData.town : '',
        Validators.compose([Validators.required, Validators.maxLength(35), NoEmptyString]),
      ),
      city: new UntypedFormControl(
        this.invoiceAddressData ? this.invoiceAddressData.city : '',
        null,
      ),
      country: new UntypedFormControl(
        this.invoiceAddressData ? this.invoiceAddressData.country : '',
        Validators.compose([null, Validators.maxLength(35)]),
      ),
      postCode: new UntypedFormControl(
        this.invoiceAddressData ? this.invoiceAddressData.postCode : '',
        Validators.compose([Validators.required, Validators.maxLength(8), NoEmptyString]),
      ),
      invoiceCompanyName: new UntypedFormControl(
        this.invoiceAddressData ? this.invoiceAddressData.invoiceCompanyName : null,
        Validators.maxLength(100),
      ),
      invoiceContactFirstName: new UntypedFormControl(
        this.invoiceAddressData ? this.invoiceAddressData.invoiceContactFirstName : null,
        Validators.maxLength(35),
      ),
      invoiceContactLastName: new UntypedFormControl(
        this.invoiceAddressData ? this.invoiceAddressData.invoiceContactLastName : null,
        Validators.maxLength(35),
      ),
      invoiceContactEmail: new UntypedFormControl(
        this.invoiceAddressData ? this.invoiceAddressData.invoiceContactEmail : null,
        Validators.compose([Validators.email, Validators.maxLength(254)]),
      ),
      invoiceContactPhone: new UntypedFormControl(
        this.invoiceAddressData ? this.invoiceAddressData.invoiceContactPhone : null,
        Validators.maxLength(20),
      ),
      invoiceContact2FirstName: new UntypedFormControl(
        this.invoiceAddressData ? this.invoiceAddressData.invoiceContact2FirstName : null,
        Validators.maxLength(35),
      ),
      invoiceContact2LastName: new UntypedFormControl(
        this.invoiceAddressData ? this.invoiceAddressData.invoiceContact2LastName : null,
        Validators.maxLength(35),
      ),
      invoiceContact2Email: new UntypedFormControl(
        this.invoiceAddressData ? this.invoiceAddressData.invoiceContact2Email : null,
        Validators.compose([Validators.email, Validators.maxLength(254)]),
      ),
      invoiceContact3FirstName: new UntypedFormControl(
        this.invoiceAddressData ? this.invoiceAddressData.invoiceContact3FirstName : null,
        Validators.maxLength(35),
      ),
      invoiceContact3LastName: new UntypedFormControl(
        this.invoiceAddressData ? this.invoiceAddressData.invoiceContact3LastName : null,
        Validators.maxLength(35),
      ),
      invoiceContact3Email: new UntypedFormControl(
        this.invoiceAddressData ? this.invoiceAddressData.invoiceContact3Email : null,
        Validators.compose([Validators.email, Validators.maxLength(254)]),
      ),
      purchaseOrderNumber: new UntypedFormControl(
        this.invoiceAddressData ? this.invoiceAddressData.purchaseOrderNumber : null,
        Validators.maxLength(100),
      ),
      organisationId: new UntypedFormControl(
        this.invoiceAddressData ? this.invoiceAddressData.organisationId : this.currentUserOrgId,
      ),
      paymentTermsType: new UntypedFormControl(
        this.invoiceAddressData ? this.invoiceAddressData.paymentTermsType : null,
      ),
      paymentTermsDays: new UntypedFormControl(
        this.invoiceAddressData ? this.invoiceAddressData.paymentTermsDays : null,
      ),
      invoicePeriod: new UntypedFormControl(
        this.invoiceAddressData ? this.invoiceAddressData.invoicePeriod : 0,
      ),
      billingWaitDays: new UntypedFormControl(
        this.invoiceAddressData ? this.invoiceAddressData.billingWaitDays : 0,
      ),
      approvalDate: new UntypedFormControl(
        this.invoiceAddressData ? this.invoiceAddressData.approvalDate : null,
      ),
      revolutCounterpartyId: new UntypedFormControl(
        this.invoiceAddressData ? this.invoiceAddressData.revolutCounterpartyId : null,
      ),
      revolutLastUpdated: new UntypedFormControl(
        this.invoiceAddressData ? this.invoiceAddressData.revolutLastUpdated : null,
      ),
      xeroContactId: new UntypedFormControl(
        this.invoiceAddressData ? this.invoiceAddressData.xeroContactId : null,
      ),
      xeroContactUpdatedOn: new UntypedFormControl(
        this.invoiceAddressData ? this.invoiceAddressData.xeroContactUpdatedOn : null,
      ),
      lastUpdated: new UntypedFormControl(
        this.invoiceAddressData ? this.invoiceAddressData.lastUpdated : null,
      ),
    };

    this.invoiceAddressFormGroup = new UntypedFormGroup({
      addressLine1: this.invoiceAddressForm.addressLine1,
      addressLine2: this.invoiceAddressForm.addressLine2,
      addressLine3: this.invoiceAddressForm.addressLine3,
      town: this.invoiceAddressForm.town,
      city: this.invoiceAddressForm.city,
      country: this.invoiceAddressForm.country,
      postCode: this.invoiceAddressForm.postCode,
      invoiceCompanyName: this.invoiceAddressForm.invoiceCompanyName,
      invoiceContactFirstName: this.invoiceAddressForm.invoiceContactFirstName,
      invoiceContactLastName: this.invoiceAddressForm.invoiceContactLastName,
      invoiceContactEmail: this.invoiceAddressForm.invoiceContactEmail,
      invoiceContactPhone: this.invoiceAddressForm.invoiceContactPhone,
      invoiceContact2FirstName: this.invoiceAddressForm.invoiceContact2FirstName,
      invoiceContact2LastName: this.invoiceAddressForm.invoiceContact2LastName,
      invoiceContact2Email: this.invoiceAddressForm.invoiceContact2Email,
      invoiceContact3FirstName: this.invoiceAddressForm.invoiceContact3FirstName,
      invoiceContact3LastName: this.invoiceAddressForm.invoiceContact3LastName,
      invoiceContact3Email: this.invoiceAddressForm.invoiceContact3Email,
      paymentTermsType: this.invoiceAddressForm.paymentTermsType,
      paymentTermsDays: this.invoiceAddressForm.paymentTermsDays,
      purchaseOrderNumber: this.invoiceAddressForm.purchaseOrderNumber,
      invoicePeriod: this.invoiceAddressForm.invoicePeriod,
      billingWaitDays: this.invoiceAddressForm.billingWaitDays,
      organisationId: this.invoiceAddressForm.organisationId,
      approvalDate: this.invoiceAddressForm.approvalDate,
      revolutCounterpartyId: this.invoiceAddressForm.revolutCounterpartyId,
      revolutLastUpdated: this.invoiceAddressForm.revolutLastUpdated,
      xeroContactId: this.invoiceAddressForm.xeroContactId,
      xeroContactUpdatedOn: this.invoiceAddressForm.xeroContactUpdatedOn,
      lastUpdated: this.invoiceAddressForm.lastUpdated,
    });
    this.renderView = true;
  }

  // load invoiceAddressForms invoice addresses page, showing the associated departments / organisations using the invoice address
  getInvoiceAddressData() {
    //get invoice address page
    this.invoiceAddressService
      .get(this.invoiceAddressId)
      .then((res) => {
        this.invoiceAddressData = res;
        this.currentOrgName = res.organisationName;
        this.initialiseInvoiceAddressForm();
      })
      .catch((error) => {
        let msg = 'Error loading requested details';
        this.messageService.add(Message.Error(`${msg}`));
      });
  }

  invoiceAddressSameAsAbove() {
    this.invoiceAddressFormGroup.patchValue({
      invoiceAddress: {
        addressLine1: this.invoiceAddressForm.addressLine1.value.toString(),
        addressLine2: this.invoiceAddressForm.addressLine2.value.toString(),
        addressLine3: this.invoiceAddressForm.addressLine3.value.toString(),
        town: this.invoiceAddressForm.town.value,
        city: this.invoiceAddressForm.city.value,
        country: this.invoiceAddressForm.country.value,
        postCode: this.invoiceAddressForm.postCode.value,
      },
    });
  }
  save(formValues) {
    // set referring Customer Department Id, or OrganisationId
    if (!this.isSaving) {
      if (formValues.paymentTermsType == -1) formValues.paymentTermsType == null;
      formValues.id = this.invoiceAddressId;
      if (!formValues.organisationId) formValues.organisationId = this.currentUserOrgId;
      this.isSaving = true;
      this.invoiceAddressService
        .save(formValues)
        .then((res) => {
          this.isSaving = false;
          if (res != undefined && res != null) {
            var param: any = {};
            if (this.customerorgId) {
              param.orgid = this.customerorgId;
            }
            this.router.navigate(['app/invoiceaddresses'], { queryParams: param });
          }
        })
        .catch((error) => {
          let msg =
            error.error && error.error.Message
              ? error.error.Message
              : 'Error saving invoice address';
          this.messageService.add(Message.Error(`${msg}`));
          this.isSaving = false;
        });
    }
  }
  cancelOperation(event) {
    var param: any = {};
    if (this.customerorgId) {
      param.orgid = this.customerorgId;
    }
    this.router.navigate(['app/invoiceaddresses'], { queryParams: param });
  }

  initAddressAutoCompletion(): void {
    const invoiceAddressFields = [
      { element: 'invoice_addressLine1', field: '', parentContainer: '', showSavedSites: false },
      { element: 'invoice_addressLine2', field: '', parentContainer: '', showSavedSites: false },
      { element: 'invoice_town', field: '', parentContainer: '', showSavedSites: false },
      { element: 'invoice_city', field: '', parentContainer: '', showSavedSites: false },
      { element: 'invoice_postcode', field: '', parentContainer: '', showSavedSites: false },
    ];

    this.addressAutoCompletionService.useWithDefaultAddressResponse(
      invoiceAddressFields,
      (address) => {
        this.invoiceAddressFormGroup.patchValue({
          addressLine1: address.line_1,
          addressLine2: address.line_2,
          addressLine3: address.line_3,
          town: address.town_or_city,
          postCode: address.postcode,
        });
      },
    );
  }
}
